import { ApiError } from './client'
import type { ApiClient } from './client'
import type { Locale, Organization } from './types'

/**
 * The caller's own organization.
 *
 * There is no id in any of these paths, and that is the tenancy rule showing
 * through the API surface rather than an omission. The organization comes from
 * the staff principal on the token; a route that took one from the request
 * would be a route that could be pointed at somebody else's.
 *
 * ## Both methods unwrap an envelope, and that used to be the settings crash
 *
 * `GET /v1/org` does not answer with an organization. It answers with
 * `{ organization, user, permissions }`, because the console needs all three on
 * the first screen it draws and one round trip is better than three. This
 * module used to declare the response as `Organization` and hand it straight
 * back, so every caller was holding the envelope and reading `name` and
 * `locales` off an object that has neither. Nothing failed at the boundary: the
 * types agreed, the request returned 200, and the screen died four functions
 * later on `Cannot read properties of undefined (reading 'length')`. `PATCH`
 * has the same shape for the same reason and had the same bug.
 *
 * So the wire shape is declared separately from the console's shape and the
 * unwrapping happens here, once. A shape mismatch is now a sentence about the
 * server rather than a stack trace inside a component.
 *
 * ## There is no `locales` on an organization, and there is no `DELETE`
 *
 * Two things this module used to offer that the platform has never had. The
 * languages a programme runs in belong to the **project**: a project carries
 * `locales` and `defaultLocale` and an organization carries only
 * `defaultLocale`, which is what a project falls back to when it does not say.
 * That is the right way round, because languages are a fact about a community
 * and an organization can be running maternal health in Hausa in Kano and cash
 * transfer in Kanuri in Borno at the same time. An organization-level list
 * would either duplicate those or quietly contradict them.
 *
 * `remove()` named `DELETE /v1/org`, which no route serves. The repository has
 * an `erase`, deliberately not exposed: closing an organization is an operator
 * action taken knowingly with the redirects arranged, not a button on a
 * settings screen. A console method for a route that does not exist is a 404
 * waiting for whoever first wires a button to it, so it is gone rather than
 * left compiling.
 */

/** What `GET /v1/org` and `PATCH /v1/org` actually put on the wire. */
interface OrganizationWire {
  id: string
  name: string
  slug: string
  status: 'active' | 'suspended'
  /** What a project falls back to when it does not name its own. */
  defaultLocale: Locale
  createdAt: string
  updatedAt: string
}

/** `GET /v1/org`. Three answers in one response, and the console reads all three. */
interface SelfResponse {
  organization?: OrganizationWire
}

/** `PATCH /v1/org`. The same envelope with only the organization in it. */
interface UpdateResponse {
  organization?: OrganizationWire
}

export interface OrganizationInput {
  name?: string
  /**
   * A BCP-47 tag. The server validates the shape loosely on purpose, and so
   * does the screen: `ha`, `en-NG`, `kr` and `fuv` are all real answers and a
   * fixed dropdown of the two the mobile app speaks today would make this field
   * lie about what the platform supports.
   */
  defaultLocale?: Locale
}

export function orgApi(http: ApiClient) {
  return {
    async get(): Promise<Organization> {
      return unwrap(await http.request<SelfResponse>('/v1/org'))
    },

    async update(input: OrganizationInput): Promise<Organization> {
      return unwrap(
        await http.request<UpdateResponse>('/v1/org', { method: 'PATCH', body: input }),
      )
    },
  }
}

/**
 * Reads the organization out of the envelope, or says what was wrong.
 *
 * A 200 whose body is the wrong shape is the failure this whole module exists
 * to have caught, and catching it means refusing to return half an object. A
 * cast would compile and hand a component an `Organization` with `undefined`
 * where its name should be, which is the exact bug that put "This screen
 * stopped working" in front of somebody. The error is shaped as a real
 * `internal` so that a caller which already handles a failed request handles
 * this too, without a second kind of check that somebody will forget.
 *
 * This is the shape a version-skewed server returns after a deploy that renames
 * a field, which is precisely the moment nobody is looking for a client bug.
 */
function unwrap(response: SelfResponse | UpdateResponse): Organization {
  const wire = response?.organization
  if (wire === undefined || typeof wire.id !== 'string') {
    throw new ApiError({
      status: 502,
      code: 'internal',
      message: 'Namu answered with something this console could not read.',
      recovery: 'retry',
    })
  }

  return {
    id: wire.id,
    name: wire.name,
    slug: wire.slug,
    defaultLocale: wire.defaultLocale,
    createdAt: wire.createdAt,
  }
}

export type OrgApi = ReturnType<typeof orgApi>
